// =============================================================================
// client-evolution.ts — Helpers PURS de l'évolution nominative par client (R25-COMPARE, PHASE R).
// =============================================================================
// Résolution FRONT du périmètre client + transposition profil × temps. AUCUN appel réseau, AUCUNE règle
// métier serveur : le back reste STATELESS sur le périmètre client (invariant n°2). Testable en isolation.
//
// C5 (SECOPS BLOQUANT) — prédicat de filtrage client = « clientScopes INCLUT le SIREN sélectionné ». On
// N'UTILISE PAS la sémantique `.some(intersection)` de use-prior-eligible-campaigns.ts:90-92 (intersection
// avec l'ensemble des SIREN de la campagne COURANTE) : ici, on isole UN client sélectionné. Un multi-SIREN
// (campagne couvrant plusieurs clients) est inclus SI ET SEULEMENT SI il contient le SIREN sélectionné ;
// les campagnes des AUTRES clients sont EXCLUES de la série.

import type { RankClientEvolutionCampaignDto } from '@/api/rank-admin-client';

/** Forme minimale d'un scope client (miroir ClientScopeDto — seul le SIREN nous intéresse ici). */
export interface ClientScopeLike {
  readonly clientSiren: string;
}

/** Forme minimale d'un détail de campagne exploité ici (id + scopes client + intitulé optionnel). */
export interface CampaignDetailLike {
  readonly campaignId: string;
  readonly intitule?: string;
  readonly clientScopes: readonly ClientScopeLike[];
}

/**
 * Les SIREN clients DISTINCTS présents dans l'ensemble des campagnes (peuplent le sélecteur). Triés.
 */
export function availableClientSirens(
  details: readonly CampaignDetailLike[],
): readonly string[] {
  const sirens = new Set<string>();
  for (const d of details) {
    for (const s of d.clientScopes) {
      sirens.add(s.clientSiren);
    }
  }
  return [...sirens].sort((a, b) => a.localeCompare(b));
}

/**
 * C5 — Les campaignId dont les `clientScopes` INCLUENT le SIREN sélectionné. Prédicat STRICT d'inclusion
 * (`=== selectedSiren`), JAMAIS une intersection d'ensembles. Les campagnes d'AUTRES clients sont EXCLUES.
 * `selectedSiren` vide ⇒ aucune campagne (fail-closed : pas de client sélectionné, pas de série).
 */
export function campaignIdsForClient(
  details: readonly CampaignDetailLike[],
  selectedSiren: string,
): readonly string[] {
  if (selectedSiren === '') {
    return [];
  }
  return details
    .filter((d) => d.clientScopes.some((s) => s.clientSiren === selectedSiren))
    .map((d) => d.campaignId);
}

// --- Transposition profil × temps (table transposée, tri chronologique) --------------------------------

/** Une colonne de la table transposée = une campagne FIGÉE (chronologique). */
export interface EvolutionColumn {
  readonly campaignId: string;
  /** Instant du figement (ISO 8601, non-null : seules les campagnes figées deviennent colonnes). */
  readonly frozenAt: string;
  /** Intitulé lisible (si résolu côté détail campagne), sinon l'id. */
  readonly label: string;
}

/** Une cellule de la table transposée, alignée sur une colonne. `present=false` ⇒ TROU (couple absent). */
export interface EvolutionMatrixCell {
  readonly present: boolean;
  readonly tjmRetenuMedianEur: number | null;
  readonly retainedCount: number;
  readonly raisonSociale: string;
}

/** Une ligne de la table transposée = un couple profil × niveau, ses cellules alignées sur les colonnes. */
export interface EvolutionRow {
  readonly profileCode: string;
  readonly seniorityLevel: string;
  readonly cells: readonly EvolutionMatrixCell[];
}

/** La matrice transposée complète (colonnes chronologiques + lignes couples). */
export interface EvolutionMatrix {
  readonly columns: readonly EvolutionColumn[];
  readonly rows: readonly EvolutionRow[];
}

/** Clé de couple profil × niveau. */
function coupleKey(profileCode: string, seniorityLevel: string): string {
  return `${profileCode}::${seniorityLevel}`;
}

/** Cellule « trou » (couple absent de cette campagne) — affiché en tiret, JAMAIS 0 €. */
const HOLE: EvolutionMatrixCell = {
  present: false,
  tjmRetenuMedianEur: null,
  retainedCount: 0,
  raisonSociale: '',
};

/**
 * Construit la table TRANSPOSÉE (profil × temps) à partir des blocs d'évolution :
 *   1. EXCLUT les campagnes NON figées (`frozenAt === null`) — seules les FIGÉES entrent dans la série ;
 *   2. TRIE les colonnes par `frozenAt` CROISSANT (chronologique — DoD R3) ;
 *   3. lignes = couples (profil × niveau) DISTINCTS sur l'ensemble des campagnes, triés ;
 *   4. cellule = médiane de la campagne pour ce couple, ou TROU si le couple est absent de cette campagne
 *      (ex. présent en v1, absent v2, présent v3 ⇒ [v1, —, v3]).
 * `labels` : intitulés lisibles par campaignId (optionnel).
 */
export function buildEvolutionMatrix(
  campaigns: readonly RankClientEvolutionCampaignDto[],
  labels?: ReadonlyMap<string, string>,
): EvolutionMatrix {
  // (1)+(2) colonnes = campagnes figées, chronologiques.
  const frozen = campaigns
    .filter((c) => c.frozenAt !== null)
    .slice()
    .sort((a, b) => (a.frozenAt as string).localeCompare(b.frozenAt as string));

  const columns: readonly EvolutionColumn[] = frozen.map((c) => ({
    campaignId: c.campaignId,
    frozenAt: c.frozenAt as string,
    label: labels?.get(c.campaignId) ?? c.campaignId,
  }));

  // (3) couples DISTINCTS + index par campagne (coupleKey → cell) pour la lecture O(1).
  const couples = new Map<string, { profileCode: string; seniorityLevel: string }>();
  const cellByCampaign = new Map<string, Map<string, RankClientEvolutionCampaignDto['cells'][number]>>();
  for (const c of frozen) {
    const idx = new Map<string, RankClientEvolutionCampaignDto['cells'][number]>();
    for (const cell of c.cells) {
      const k = coupleKey(cell.profileCode, cell.seniorityLevel);
      couples.set(k, { profileCode: cell.profileCode, seniorityLevel: cell.seniorityLevel });
      idx.set(k, cell);
    }
    cellByCampaign.set(c.campaignId, idx);
  }

  // (4) lignes triées + cellules alignées sur les colonnes (trou si absent).
  const rows: readonly EvolutionRow[] = [...couples.values()]
    .sort(
      (a, b) =>
        a.profileCode.localeCompare(b.profileCode) ||
        a.seniorityLevel.localeCompare(b.seniorityLevel),
    )
    .map((couple) => {
      const k = coupleKey(couple.profileCode, couple.seniorityLevel);
      const cells = columns.map((col): EvolutionMatrixCell => {
        const cell = cellByCampaign.get(col.campaignId)?.get(k);
        if (cell === undefined) {
          return HOLE;
        }
        return {
          present: true,
          tjmRetenuMedianEur: cell.tjmRetenuMedianEur,
          retainedCount: cell.retainedCount,
          raisonSociale: cell.raisonSociale,
        };
      });
      return { profileCode: couple.profileCode, seniorityLevel: couple.seniorityLevel, cells };
    });

  return { columns, rows };
}
